import { useHandler } from "@redotech/react-util/hook";
import {
  CarrierAccountType,
  ICarrierAccount,
  RedoCarrierName,
  UspsDirectRateType,
} from "@redotech/redo-fulfillment-rate-common/carrier-account";
import { ICreateCarrierAccount } from "@redotech/redo-fulfillment-rate-common/create-carrier-account";
import { assertNever } from "@redotech/util/type";
import { toast } from "../../../alert";
import { CarrierInputValue } from "./carrier-input";

interface CarrierAccountResponse {
  merchantCarrierAccounts: ICarrierAccount[];
  redoChildCarrierAccounts: ICarrierAccount[];
}

function getCarrierData(
  carrier: CarrierInputValue,
): ICreateCarrierAccount["data"] {
  switch (carrier.redoCarrierName) {
    case RedoCarrierName.FIRST_MILE:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.firstMileCarrierInput.accountNumber,
        username: carrier.firstMileCarrierInput.username,
        password: carrier.firstMileCarrierInput.password,
      };
    case RedoCarrierName.AUSTRALIA_POST:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.australiaPostCarrierInput.accountNumber,
        api_key: carrier.australiaPostCarrierInput.apiKey,
        api_secret: carrier.australiaPostCarrierInput.apiSecret,
      };
    case RedoCarrierName.CANADA_POST:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.canadaPostCarrierInput.apiKey,
        contract_id: carrier.canadaPostCarrierInput.contractId,
        customer_number: carrier.canadaPostCarrierInput.customerNumber,
        has_credit_card: carrier.canadaPostCarrierInput.hasCreditCard,
      };
    case RedoCarrierName.CANADA_POST_DIRECT: {
      const apiKey = carrier.canadaPostDirectCarrierInput.apiKey.trim();
      const apiKeySeparatorIndex = apiKey.indexOf(":");
      if (
        apiKeySeparatorIndex <= 0 ||
        apiKeySeparatorIndex === apiKey.length - 1
      ) {
        throw new Error("Canada Post API key must be username:password");
      }
      const username = apiKey.slice(0, apiKeySeparatorIndex);
      const password = apiKey.slice(apiKeySeparatorIndex + 1);
      return {
        redoCarrierName: carrier.redoCarrierName,
        username,
        password,
        customer_number: carrier.canadaPostDirectCarrierInput.customerNumber,
        contract_number: carrier.canadaPostDirectCarrierInput.contractNumber,
        manifest_mode: carrier.canadaPostDirectCarrierInput.manifestMode,
      };
    }
    case RedoCarrierName.ROYAL_MAIL:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.royalMailCarrierInput.accountNumber,
        posting_location_number:
          carrier.royalMailCarrierInput.postingLocationNumber,
        username: carrier.royalMailCarrierInput.username,
      };
    case RedoCarrierName.ROYAL_MAIL_V3:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.royalMailV3CarrierInput.accountNumber,
        oba_email_address: carrier.royalMailV3CarrierInput.obaEmailAddress,
        posting_location_number:
          carrier.royalMailV3CarrierInput.postingLocationNumber,
        oba_access_code:
          carrier.royalMailV3CarrierInput.obaAccessCode || undefined,
        registered_billing_postcode:
          carrier.royalMailV3CarrierInput.registeredBillingPostcode ||
          undefined,
        contact_name: carrier.royalMailV3CarrierInput.contactName,
        contact_phone: carrier.royalMailV3CarrierInput.contactPhone,
        dispatch_street1: carrier.royalMailV3CarrierInput.dispatchStreet1,
        dispatch_street2:
          carrier.royalMailV3CarrierInput.dispatchStreet2 || undefined,
        dispatch_city: carrier.royalMailV3CarrierInput.dispatchCity,
        dispatch_zip: carrier.royalMailV3CarrierInput.dispatchZip,
        dispatch_state: carrier.royalMailV3CarrierInput.dispatchState,
        dispatch_country: carrier.royalMailV3CarrierInput.dispatchCountry,
      };
    case RedoCarrierName.VEHO:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.vehoCarrierInput.productionApiKey,
      };
    case RedoCarrierName.APC:
      return {
        redoCarrierName: carrier.redoCarrierName,
        shipper_id: carrier.apcCarrierInput.shipperId,
        password: carrier.apcCarrierInput.password,
        facility: carrier.apcCarrierInput.facility,
      };
    case RedoCarrierName.DHL_ECS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        client_id: carrier.dhlCarrierInput.clientId,
        client_secret: carrier.dhlCarrierInput.clientSecret,
        distribution_center: carrier.dhlCarrierInput.distributionCenter,
        pickup_id: carrier.dhlCarrierInput.pickupId,
      };
    case RedoCarrierName.DHL_EXPRESS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.dhlExpressCarrierInput.accountNumber,
        country_code: carrier.dhlExpressCarrierInput.countryCode,
        password: carrier.dhlExpressCarrierInput.password,
        site_id: carrier.dhlExpressCarrierInput.siteId,
      };
    case RedoCarrierName.UPS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.upsCarrierInput.accountNumber,
      };
    case RedoCarrierName.UPS_SUREPOST:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.upsSurepostCarrierInput.accountNumber,
      };
    case RedoCarrierName.UPS_MAIL_INNOVATIONS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.upsMailInnovationsCarrierInput.accountNumber,
        user_id: carrier.upsMailInnovationsCarrierInput.userId,
        password: carrier.upsMailInnovationsCarrierInput.password,
        customer_id: carrier.upsMailInnovationsCarrierInput.customerId,
        customer_guid: carrier.upsMailInnovationsCarrierInput.customerGuid,
      };
    case RedoCarrierName.USPS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        company_name: carrier.uspsCarrierInput.companyName,
        address_street: carrier.uspsCarrierInput.addressStreet,
        address_city: carrier.uspsCarrierInput.addressCity,
        address_state: carrier.uspsCarrierInput.addressState,
        address_zip: carrier.uspsCarrierInput.addressZip,
        phone: carrier.uspsCarrierInput.phone,
        email: carrier.uspsCarrierInput.email,
        shipper_id: carrier.uspsCarrierInput.shipperId,
      };
    case RedoCarrierName.FEDEX_CROSS_BORDER:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.fedexCrossBorderCarrierInput.apiKey,
        connect_hub_origin:
          carrier.fedexCrossBorderCarrierInput.connectHubOrigin,
      };
    case RedoCarrierName.FEDEX:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.fedexCarrierInput.accountNumber,
        shipping_city: carrier.fedexCarrierInput.shippingCity,
        shipping_country_code: carrier.fedexCarrierInput.shippingCountryCode,
        shipping_postal_code: carrier.fedexCarrierInput.shippingPostalCode,
        shipping_state: carrier.fedexCarrierInput.shippingState,
        shipping_streets: carrier.fedexCarrierInput.shippingStreets,
        corporate_city: carrier.fedexCarrierInput.corporateCity,
        corporate_company_name: carrier.fedexCarrierInput.corporateCompanyName,
        corporate_country_code: carrier.fedexCarrierInput.corporateCountryCode,
        corporate_email_address:
          carrier.fedexCarrierInput.corporateEmailAddress,
        corporate_first_name: carrier.fedexCarrierInput.corporateFirstName,
        corporate_job_title: carrier.fedexCarrierInput.corporateJobTitle,
        corporate_last_name: carrier.fedexCarrierInput.corporateLastName,
        corporate_phone_number: carrier.fedexCarrierInput.corporatePhoneNumber,
        corporate_postal_code: carrier.fedexCarrierInput.corporatePostalCode,
        corporate_state: carrier.fedexCarrierInput.corporateState,
        corporate_streets: carrier.fedexCarrierInput.corporateStreets,
      };
    case RedoCarrierName.FEDEX_SMARTPOST:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.fedexSmartpostCarrierInput.accountNumber,
        hub_id: carrier.fedexSmartpostCarrierInput.hubId,
        shipping_city: carrier.fedexSmartpostCarrierInput.shippingCity,
        shipping_country_code:
          carrier.fedexSmartpostCarrierInput.shippingCountryCode,
        shipping_postal_code:
          carrier.fedexSmartpostCarrierInput.shippingPostalCode,
        shipping_state: carrier.fedexSmartpostCarrierInput.shippingState,
        shipping_streets: carrier.fedexSmartpostCarrierInput.shippingStreets,
        corporate_city: carrier.fedexSmartpostCarrierInput.corporateCity,
        corporate_company_name:
          carrier.fedexSmartpostCarrierInput.corporateCompanyName,
        corporate_country_code:
          carrier.fedexSmartpostCarrierInput.corporateCountryCode,
        corporate_email_address:
          carrier.fedexSmartpostCarrierInput.corporateEmailAddress,
        corporate_first_name:
          carrier.fedexSmartpostCarrierInput.corporateFirstName,
        corporate_job_title:
          carrier.fedexSmartpostCarrierInput.corporateJobTitle,
        corporate_last_name:
          carrier.fedexSmartpostCarrierInput.corporateLastName,
        corporate_phone_number:
          carrier.fedexSmartpostCarrierInput.corporatePhoneNumber,
        corporate_postal_code:
          carrier.fedexSmartpostCarrierInput.corporatePostalCode,
        corporate_state: carrier.fedexSmartpostCarrierInput.corporateState,
        corporate_streets: carrier.fedexSmartpostCarrierInput.corporateStreets,
      };
    case RedoCarrierName.SEKO_OMNIPARCEL:
      return {
        redoCarrierName: carrier.redoCarrierName,
        returnsTestApiKey: carrier.sekoOmniParcelCarrierInput.returnsTestApiKey,
        returnsProductionApiKey:
          carrier.sekoOmniParcelCarrierInput.returnsProductionApiKey,
        outboundTestApiKey:
          carrier.sekoOmniParcelCarrierInput.outboundTestApiKey,
        outboundProductionApiKey:
          carrier.sekoOmniParcelCarrierInput.outboundProductionApiKey,
      };
    case RedoCarrierName.GLS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.glsCarrierInput.accountNumber,
        password: carrier.glsCarrierInput.password,
        username: carrier.glsCarrierInput.username,
      };
    case RedoCarrierName.ASENDIA:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.asendiaCarrierInput.accountNumber,
        password: carrier.asendiaCarrierInput.password,
        username: carrier.asendiaCarrierInput.username,
        api_key: carrier.asendiaCarrierInput.apiKey,
        carrier_facility:
          carrier.asendiaCarrierInput.carrierFacility || undefined,
        sub_account_number:
          carrier.asendiaCarrierInput.subAccountNumber || undefined,
      };
    case RedoCarrierName.SPEE_DEE:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.speeDeeCarrierInput.accountNumber,
      };
    case RedoCarrierName.AMAZON:
      return { redoCarrierName: carrier.redoCarrierName };
    case RedoCarrierName.USPS_SHIP:
      return {
        redoCarrierName: carrier.redoCarrierName,
        client_id: carrier.uspsShipCarrierInput.clientId,
        client_secret: carrier.uspsShipCarrierInput.clientSecret,
        account_number: carrier.uspsShipCarrierInput.accountNumber,
        customer_registration_id:
          carrier.uspsShipCarrierInput.customerRegistrationId,
        mailer_id: carrier.uspsShipCarrierInput.mailerId,
        manifest_id: carrier.uspsShipCarrierInput.manifestId,
      };
    case RedoCarrierName.PASSPORT_GLOBAL:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.passportGlobalCarrierInput.apiKey,
        company_name: carrier.passportGlobalCarrierInput.companyName,
        email: carrier.passportGlobalCarrierInput.email,
        name: carrier.passportGlobalCarrierInput.name,
        phone: carrier.passportGlobalCarrierInput.phone,
      };
    case RedoCarrierName.ETA_EXPRESS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.etaExpressCarrierInput.apiKey,
      };
    case RedoCarrierName.EPOST_GLOBAL:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.epostGlobalCarrierInput.account_number,
      };
    case RedoCarrierName.USPS_DIRECT:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.uspsDirectCarrierInput.accountNumber,
        customer_registration_id:
          carrier.uspsDirectCarrierInput.customerRegistrationId,
        mailer_id: carrier.uspsDirectCarrierInput.mailerId,
        manifest_mailer_id: carrier.uspsDirectCarrierInput.manifestMailerId,
        rate_type: UspsDirectRateType.MERCHANT_RATES,
      };
    case RedoCarrierName.ONTRAC:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_id: carrier.onTracCarrierInput.apiId,
        api_key: carrier.onTracCarrierInput.apiKey,
        facility: carrier.onTracCarrierInput.facility,
        injection_city: carrier.onTracCarrierInput.injectionCity,
        injection_contact: carrier.onTracCarrierInput.injectionContact,
        injection_country: carrier.onTracCarrierInput.injectionCountry,
        injection_facility_code:
          carrier.onTracCarrierInput.injectionFacilityCode,
        injection_instruction: carrier.onTracCarrierInput.injectionInstruction,
        injection_phone: carrier.onTracCarrierInput.injectionPhone,
        injection_state: carrier.onTracCarrierInput.injectionState,
        injection_street1: carrier.onTracCarrierInput.injectionStreet1,
        injection_street2: carrier.onTracCarrierInput.injectionStreet2,
        injection_zip: carrier.onTracCarrierInput.injectionZip,
        pickup_type: carrier.onTracCarrierInput.pickupType,
      };
    case RedoCarrierName.PUROLATOR:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.purolatorCarrierInput.accountNumber,
        api_key: carrier.purolatorCarrierInput.productionApiKey,
        password: carrier.purolatorCarrierInput.productionApiKeyPassword,
      };
    case RedoCarrierName.CANPAR_EXPRESS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        account_number: carrier.canparCarrierInput.accountNumber,
        username: carrier.canparCarrierInput.username,
        password: carrier.canparCarrierInput.password,
      };
    case RedoCarrierName.UNIUNI:
      return {
        redoCarrierName: carrier.redoCarrierName,
        client_id: carrier.uniUniCarrierInput.clientId,
        client_secret: carrier.uniUniCarrierInput.clientSecret,
        customer_number: carrier.uniUniCarrierInput.customerNumber,
        pickup_warehouse: carrier.uniUniCarrierInput.pickupWarehouse,
      };
    case RedoCarrierName.STALLION_EXPRESS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_token: carrier.stallionExpressCarrierInput.apiToken,
      };
    case RedoCarrierName.CHITCHATS:
      return {
        redoCarrierName: carrier.redoCarrierName,
        client_id: carrier.chitChatsCarrierInput.clientId,
        api_token: carrier.chitChatsCarrierInput.apiToken,
      };
    case RedoCarrierName.SENDCLOUD:
      return {
        redoCarrierName: carrier.redoCarrierName,
        region: carrier.sendcloudCarrierInput.region,
        public_key: carrier.sendcloudCarrierInput.publicKey,
        secret_key: carrier.sendcloudCarrierInput.secretKey,
      };
    case RedoCarrierName.JITSU:
      return {
        redoCarrierName: carrier.redoCarrierName,
        api_key: carrier.jitsuCarrierInput.apiKey,
      };
    default:
      assertNever(carrier.redoCarrierName);
  }
}

export function useCarriers({
  carrierAccountType,
  setSavedCarriers,
  createCarrierAccount,
  deleteCarrierAccount,
}: {
  carrierAccountType: CarrierAccountType;
  setSavedCarriers: (carriers: ICarrierAccount[]) => void;
  createCarrierAccount: (
    carrier: ICreateCarrierAccount,
  ) => Promise<CarrierAccountResponse>;
  deleteCarrierAccount: ({
    carrierAccount,
  }: {
    carrierAccount: ICarrierAccount;
  }) => Promise<CarrierAccountResponse>;
}) {
  const getSavedCarriers = useHandler((response: CarrierAccountResponse) => {
    switch (carrierAccountType) {
      case CarrierAccountType.MERCHANT:
        return response.merchantCarrierAccounts;
      case CarrierAccountType.REDO_CHILD:
        return response.redoChildCarrierAccounts;
      default:
        assertNever(carrierAccountType);
    }
  });

  const addCarrier = useHandler(async (carrier: CarrierInputValue) => {
    try {
      const carrierToAdd: ICreateCarrierAccount = {
        name: carrier.name,
        carrierAccountType,
        data: getCarrierData(carrier),
      };

      const response = await createCarrierAccount(carrierToAdd);
      setSavedCarriers(getSavedCarriers(response));
      return { success: true };
    } catch (error) {
      const clientError = error as { statusCode: number; message: string };
      if (clientError.statusCode === 422) {
        toast(clientError.message, { variant: "error" });
      } else {
        toast(
          "Your input was rejected, please make sure everything is correct",
          { variant: "error" },
        );
      }
      return { success: false };
    }
  });

  const deleteCarrier = useHandler(async (carrierAccount: ICarrierAccount) => {
    try {
      const response = await deleteCarrierAccount({ carrierAccount });
      setSavedCarriers(getSavedCarriers(response));
    } catch (error) {
      toast("Failed to delete carrier account", { variant: "error" });
    }
  });

  const authenticateCarrier = useHandler(
    async (carrierAccount: ICarrierAccount) => {
      if ("oauthUrl" in carrierAccount && !!carrierAccount.oauthUrl) {
        window.open(carrierAccount.oauthUrl, "_blank");
      }
    },
  );

  return { addCarrier, deleteCarrier, authenticateCarrier };
}
